import { useState, useEffect } from 'react';
import { fetchWithAuth, fetchWithCorpId } from '../utils/api';
import { useAuth } from '../utils/auth';
import { useNavigate, Link } from 'react-router-dom';
import NotificationBell from './NotificationBell';

function TopBar({ selectedCorp }) {
  const [logoUrl, setLogoUrl] = useState(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [profileName, setProfileName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const { logout, token, getUserRoles } = useAuth();
  const navigate = useNavigate();

  const parseJwt = (t) => { try { return JSON.parse(atob(t.split('.')[1])); } catch { return null; } };
  const tokenData = token ? parseJwt(token) : null;
  const givenName = tokenData?.given_name || '';
  const familyName = tokenData?.family_name || '';
  const sub = tokenData?.sub || '';
  const jwtName = [givenName, familyName].filter(Boolean).join(' ') || sub || 'User';
  const fullName = profileName || jwtName;
  const initials = profileName
    ? profileName.trim().split(/\s+/).slice(0, 2).map(w => w.charAt(0)).join('').toUpperCase()
    : (givenName && familyName)
      ? `${givenName.charAt(0)}${familyName.charAt(0)}`.toUpperCase()
      : sub ? sub.charAt(0).toUpperCase() : 'U';
  const roles = getUserRoles();
  const roleLabel = roles.includes('super') ? 'Super Admin'
    : roles.includes('admin') ? 'Admin'
      : roles.includes('employer') ? 'Employer'
        : roles.includes('employee') ? 'Employee'
          : 'User';

  const handleLogout = () => {
    try { window.localStorage.clear(); window.sessionStorage && window.sessionStorage.clear(); } catch (_) { }
    logout();
    navigate('/on/login');
  };

  useEffect(() => {
    const loadLogo = async () => {
      if (!selectedCorp?.id) { setLogoUrl(null); return; }
      try {
        const response = await fetchWithAuth(`/corps/${selectedCorp.id}/logo`, { headers: { Accept: 'image/*' } });
        if (!response.ok) { setLogoUrl(null); return; }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setLogoUrl(url);
        setCurrentLogoUrl(url);
      } catch { setLogoUrl(null); }
    };
    loadLogo();
    return () => { if (currentLogoUrl) URL.revokeObjectURL(currentLogoUrl); };
  }, [selectedCorp]);

  // Prefer the profile username when the user has set one. The endpoint
  // may not exist on older backends, so any failure falls back silently to
  // the JWT-derived name.
  useEffect(() => {
    let cancelled = false;
    fetchWithCorpId('/profile', { silent: true })
      .then(profile => {
        if (!cancelled && profile?.username) setProfileName(profile.username);
      })
      .catch(() => { /* backend without /profile: keep the JWT name */ });
    return () => { cancelled = true; };
  }, []);

  // Show the profile photo in the pill when one exists. A 404 (no photo yet)
  // or an older backend keeps the initials.
  useEffect(() => {
    let cancelled = false;
    let url = null;
    const loadAvatar = async () => {
      try {
        const response = await fetchWithCorpId('/profile/avatar', { headers: { Accept: 'image/*' }, silent: true });
        if (!response.ok || cancelled) return;
        const blob = await response.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setAvatarUrl(url);
      } catch { /* keep the initials */ }
    };
    loadAvatar();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, []);

  return (
    <header style={{
      background: 'var(--canvas)',
      height: '64px',
      padding: '0 32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      zIndex: 100,
      flexShrink: 0,
      borderBottom: '1px solid var(--hairline)',
      boxShadow: 'none',
    }}>

      {/* Left: One Nexus brand mark + wordmark (or corp logo + name when
          selected). Links back to the dashboard from anywhere. */}
      <Link
        to="/on/dashboard"
        aria-label="Go to dashboard"
        style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="logo"
            style={{ height: '32px', width: '32px', objectFit: 'contain', borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <svg viewBox="0 0 100 100" width="30" height="30" aria-hidden="true" style={{ flexShrink: 0 }}>
            <g>
              <path d="M 83.8 40.9 A 35 35 0 1 1 59.1 16.2" fill="none" stroke="var(--brand-navy)" strokeWidth="10" strokeLinecap="butt"/>
              <circle cx="74.7" cy="25.3" r="8" fill="var(--brand-blue)"/>
              <path d="M 37 65 L 37 45 A 13 13 0 0 1 63 45 L 63 65" fill="none" stroke="var(--brand-navy)" strokeWidth="10" strokeLinecap="butt"/>
            </g>
          </svg>
        )}
        {selectedCorp ? (
          <div style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '15px',
            fontWeight: 600,
            letterSpacing: '0.1px',
            color: 'var(--ink)',
            lineHeight: 1.2,
          }}>
            {selectedCorp.name}
          </div>
        ) : (
          <span style={{
            fontFamily: 'var(--font-wordmark)',
            fontSize: '18px',
            fontWeight: 800,
            letterSpacing: '-0.5px',
            lineHeight: 1,
          }}>
            <span style={{ color: 'var(--brand-blue)' }}>One</span>
            <span style={{ color: 'var(--brand-navy)' }}> Nexus</span>
          </span>
        )}
      </Link>

      {/* Right: Notifications + user cluster + Sign out */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <NotificationBell />
        <button
          type="button"
          onClick={() => navigate('/on/settings')}
          aria-label="Profile settings"
          style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            padding: '4px 16px 4px 4px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--surface-soft)',
            border: '1px solid var(--hairline)',
            cursor: 'pointer', textAlign: 'left',
            transition: 'background 0.16s, border-color 0.16s',
          }}
          onMouseOver={e => { e.currentTarget.style.background = 'var(--surface-card)'; e.currentTarget.style.borderColor = 'var(--surface-cream-strong)'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'var(--surface-soft)'; e.currentTarget.style.borderColor = 'var(--hairline)'; }}
        >
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
            background: 'var(--coral)', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-sans)',
            fontSize: '12px', fontWeight: 500, color: 'var(--on-primary)',
            letterSpacing: '0.2px',
          }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : initials}
          </div>
          <div>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', fontWeight: 500, color: 'var(--ink)', lineHeight: 1.2 }}>{fullName}</div>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: '11px', color: 'var(--muted)', fontWeight: 400, letterSpacing: '0.3px' }}>{roleLabel}</div>
          </div>
        </button>

        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            height: '40px', padding: '0 16px', borderRadius: 'var(--radius-md)',
            background: 'transparent', border: '1px solid var(--hairline)',
            color: 'var(--ink)', cursor: 'pointer',
            fontFamily: 'var(--font-sans)', fontSize: '14px', fontWeight: 500,
            transition: 'background 0.16s, border-color 0.16s, color 0.16s',
          }}
          onMouseOver={e => { e.currentTarget.style.background = 'var(--surface-soft)'; e.currentTarget.style.borderColor = 'var(--surface-cream-strong)'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--hairline)'; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Sign out
        </button>
      </div>
    </header>
  );
}

export default TopBar;
